import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from "@mui/material";
import React, { SyntheticEvent } from "react";
import { AttorneyCard } from "./AttorneysCard";
import "../Attorneys.scss";
import {
  ApplicationRole,
  GetUserListRequest,
  UserShortResponse,
  UsersService,
} from "../../../../api";
import { LeadsState } from "../../LeadsManagement/LeadsTables/LeadsTableConstants";
import { TabsHelpers } from "../../../../components/basic/TabsHelpers";
import { useNavigate } from "react-router-dom";
import { isUserActive, toggleUserActive } from "../../Users/userActiveActions";

interface HeadCell {
  id: string;
  label: string;
}
const headCells: readonly HeadCell[] = [
  {
    id: "name",
    label: "Name",
  },
  {
    id: "leads",
    label: "Leads",
  },
];

export default function AttorneysTable() {
  const [value, setValue] = useState<number>(0);
  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [approvedAttorneys, setApprovedAttorneys] = useState<UserShortResponse[]>([]);
  const [inactiveAttorneys, setInactiveAttorneys] = useState<UserShortResponse[]>([]);
  const [pendingAttorneys, setPendingAttorneys] = useState<UserShortResponse[]>([]);

  const navigate = useNavigate();

  const loadAttorneys = useCallback(() => {
    UsersService.postApiUsersGetUsersByRole({
      role: ApplicationRole.ATTORNEY,
    } as GetUserListRequest).then(setApprovedAttorneys);

    UsersService.postApiUsersGetUsersByRole({
      role: ApplicationRole.ATTORNEY,
      inactiveOnly: true,
    } as GetUserListRequest).then(setInactiveAttorneys);

    UsersService.postApiUsersGetUsersByRole({
      role: ApplicationRole.USER,
      includeInactive: true,
    } as GetUserListRequest).then((users) => {
      setPendingAttorneys(users.filter((user) => user.isPending));
    });
  }, []);

  useEffect(() => {
    loadAttorneys();
  }, [loadAttorneys]);

  const handleDetailsClick = (id: number) => {
    navigate(`/attorney/${id}`);
  };

  const handleCreateRegistrationLink = () => {
    navigate("/generateLink");
  };

  const handleCreateAttorney = () => {
    navigate("/attorney/new");
  };

  const handleToggleActive = async (row: UserShortResponse, isActive: boolean) => {
    await toggleUserActive(row.id!, {
      firstName: row.firstName,
      lastName: row.lastName,
      isActive,
      onSuccess: loadAttorneys,
    });
  };

  const goToLeadsFor = (row: UserShortResponse, status?: string) => {
    const attorneyId = row.id;
    const fullName = `${row.firstName} ${row.lastName}`;

    const allNames = approvedAttorneys.map((a) => `${a.firstName} ${a.lastName}`);
    const allIds = approvedAttorneys.map((a) => Number(a.id));

    const query = new URLSearchParams({
      attorneyId: String(attorneyId),
    });

    if (status) query.set("attorneyStatus", status);

    const stateData: LeadsState = {
      names: allNames,
      ids: allIds,
      selectedName: fullName,
    };

    navigate(`/attorney-leads?${query.toString()}`, { state: stateData });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <div className="row justify-content-between">
        <div className="col-12 row">
          <div className="col-6">
            <h2 className="title">Attorneys</h2>
          </div>
          <div className="col-3 d-flex justify-content-end mb-4">
            <Button variant="contained" color="primary" onClick={handleCreateAttorney}>
              Add new attorney
            </Button>
          </div>
          <div className="col-3 d-flex justify-content-end mb-4">
            <Button variant="contained" color="primary" onClick={handleCreateRegistrationLink}>
              Create registration link
            </Button>
          </div>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={value} onChange={handleTabChange}>
              <Tab label="ACTIVE ATTORNEYS" {...TabsHelpers.parseProps(0)} />
              <Tab label="PENDING APPROVAL" {...TabsHelpers.parseProps(1)} />
              <Tab label="INACTIVE" {...TabsHelpers.parseProps(2)} />
            </Tabs>
          </Box>
          <TabsHelpers.TabPanel value={value} index={0}>
            <TableContainer>
              <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
                <TableHead>
                  <TableRow>
                    {headCells.map((headCell) => (
                      <TableCell key={headCell.id}>{headCell.label}</TableCell>
                    ))}
                    <TableCell align="left">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvedAttorneys.map((row) => (
                    <TableRow tabIndex={-1} key={row.id}>
                      <TableCell align="left">
                        {row.firstName} {row.lastName}
                      </TableCell>
                      <TableCell align="left">
                        <div className="col-12 d-flex table-filters">
                          <Button
                            variant="contained"
                            color="darkGray"
                            size="small"
                            onClick={() => goToLeadsFor(row)}
                          >
                            All(
                            {Object.values(row.countByLeadStatus ?? {}).reduce(
                              (acc, count) => acc! + count!,
                              0
                            )}
                            )
                          </Button>
                          <Button
                            variant="contained"
                            color="cyan"
                            size="small"
                            onClick={() => goToLeadsFor(row, "NEW")}
                          >
                            New({row.countByLeadStatus?.new ?? 0})
                          </Button>
                          <Button
                            variant="contained"
                            color="purple"
                            size="small"
                            onClick={() => goToLeadsFor(row, "ACCEPTED")}
                          >
                            Accepted({row.countByLeadStatus?.accepted ?? 0})
                          </Button>
                          <Button
                            variant="contained"
                            color="dirtyYellow"
                            size="small"
                            onClick={() => goToLeadsFor(row, "REJECTED")}
                          >
                            Rejected({row.countByLeadStatus?.rejected ?? 0})
                          </Button>
                          <Button
                            variant="contained"
                            color="brown"
                            size="small"
                            onClick={() => goToLeadsFor(row, "LOST")}
                          >
                            Lost({row.countByLeadStatus?.lost ?? 0})
                          </Button>
                          <Button
                            variant="contained"
                            color="dirtyBlue"
                            size="small"
                            onClick={() => goToLeadsFor(row, "WON")}
                          >
                            Won(
                            {(row.countByLeadStatus?.won ?? 0) +
                              (row.countByLeadStatus?.signed ?? 0) +
                              (row.countByLeadStatus?.paid ?? 0)}
                            )
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell align="left">
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => handleDetailsClick(row.id!)}
                          >
                            View details
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleToggleActive(row, false)}
                          >
                            Deactivate
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabsHelpers.TabPanel>
          <TabsHelpers.TabPanel value={value} index={1}>
            <div className="row">
              {pendingAttorneys.length > 0 ? (
                pendingAttorneys.map((row) => (
                  <AttorneyCard
                    action={handleDetailsClick}
                    attorney={row}
                    key={row.id}
                  ></AttorneyCard>
                ))
              ) : (
                <div className="text-center caption-text mt-3">No attorneys pending review.</div>
              )}
            </div>
          </TabsHelpers.TabPanel>
          <TabsHelpers.TabPanel value={value} index={2}>
            <TableContainer>
              <Table sx={{ minWidth: 750 }} size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="left">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inactiveAttorneys.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No inactive attorneys.
                      </TableCell>
                    </TableRow>
                  ) : (
                    inactiveAttorneys.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          {row.firstName} {row.lastName}
                        </TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>
                          <Chip size="small" label="Inactive" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              onClick={() => handleDetailsClick(row.id!)}
                            >
                              View details
                            </Button>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              onClick={() => handleToggleActive(row, true)}
                            >
                              Reactivate
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabsHelpers.TabPanel>
        </div>
      </div>
    </Box>
  );
}
